import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import PinLogin from './PinLogin';
import { DashboardLayout } from './dashboard/DashboardLayout';
import TimeoutWarning from './TimeoutWarning';
// 【追加】useDashboardStoreをインポート
import { useDashboardStore } from '../store/dashboardStore';
import { Staff } from '../types';
import { useMediaQuery } from '../hooks/useMediaQuery';
import { supabase } from '../lib/supabaseClient';
import { signOutAuth } from '../lib/supabaseAuth';
import { requiresPasswordSetup } from '../lib/passwordSetup';
import { isPersonalLoginEnabled } from '../lib/personalLoginAccess';

// 予約済みパスのリスト
const RESERVED_PATHS = ['login', 'account', 'signout', 'forgot-password', 'reset-password', 'setup-password'];

export default function ClinicApp() {
  const { clinicId } = useParams<{ clinicId: string }>();
  const navigate = useNavigate();
  const isMobile = useMediaQuery('(max-width: 768px)');
  const [authLoading, setAuthLoading] = useState(true);
  const [authUserId, setAuthUserId] = useState<string | null>(null);
  const [isDeviceUser, setIsDeviceUser] = useState(false);
  const [activeStaff, setActiveStaff] = useState<Staff | null>(null);
  const [staffError, setStaffError] = useState<string | null>(null);

  // 【追加】ストアからユーザー情報とセッターを取得
  const {
    currentUser,
    setCurrentUser,
    setAuthUserId: setAuthUserIdInStore,
    setIsDeviceUser: setIsDeviceUserInStore,
    initialize,
    setCurrentClinicId,
    setShowWorkflowStaffListPanel,
    setShowDatePanel,
    setShowDetailPanel,
    setScheduleSelectedStaffId,
    setSelectedDate
  } = useDashboardStore();

  // ログイン直後の初期パネル状態をロール別に揃える（前ユーザーの残状態を持ち越さない）
  const applyInitialPanelsForRole = useCallback((staff: Staff) => {
    // まずは「全パネル閉じ」を強制（残状態の持ち越し防止）
    useDashboardStore.setState({
      // workflow
      showWorkflowStaffListPanel: false,
      showDatePanel: false,
      showSchedulePanel: false,
      showSchedulePatientListPanel: false,
      // handover
      showDetailPanel: false,
      selectedPatientId: null,
      // settings
      showSettingsPanel: false,
      showSpeechDictionaryPanel: false,
      showAllPatientListPanel: false,
      showStaffListPanel: false,
      showPatientFormPanel: false,
      showStaffFormPanel: false,
      showSettingsDatePanel: false,
      // editors
      editingPatient: null,
      editingStaff: null,
      // back navigation (mobile animation flag)
      isNavigatingBack: false,
      settingsReturningToRoot: false,
      workflowRootRevealFromSettings: false,
      settingsRootRevealFromWorkflow: false,
    } as any);

    // スマホ版: staff はカレンダー、admin/developer はスタッフ一覧をデフォルト表示
    if (isMobile) {
      if (staff.role === 'staff') {
        setScheduleSelectedStaffId(staff.id);
        setShowDatePanel(true);
        setSelectedDate(new Date());
      } else if (staff.role === 'admin' || staff.role === 'developer') {
        useDashboardStore.setState({ skipWorkflowStaffListAnimation: true });
        setShowWorkflowStaffListPanel(true);
      }
      return;
    }

    if (staff.role === 'developer') {
      setShowWorkflowStaffListPanel(true);
      return;
    }

    if (staff.role === 'staff') {
      // デモ/スタッフ: カレンダーパネルのみを表示（申し送りやスケジュールは開かない）
      setScheduleSelectedStaffId(staff.id);
      setShowDatePanel(true);
      setSelectedDate(new Date());
      return;
    }

    if (staff.role === 'admin') {
      // 管理者: すべてのパネルを閉じた状態から開始する
      setScheduleSelectedStaffId(null);
      return;
    }
  }, [
    isMobile,
    setScheduleSelectedStaffId,
    setSelectedDate,
    setShowDatePanel,
    setShowWorkflowStaffListPanel,
  ]);

  // clinicId（URL）をストアに反映（以降のDBアクセスで clinic_id を使えるようにする）
  useEffect(() => {
    setCurrentClinicId(clinicId ?? null);
  }, [clinicId, setCurrentClinicId]);

  const handleSignOut = async () => {
    try {
      await signOutAuth();
    } catch (error) {
      console.error('Sign out error:', error);
    } finally {
      setAuthUserId(null);
      setIsDeviceUser(false);
      setActiveStaff(null);
      setCurrentUser(null);
      setAuthUserIdInStore(null);
      setIsDeviceUserInStore(false);
      navigate('/login');
    }
  };

  /** device+PIN 時: 操作者を解除して PIN 選択画面へ戻る（完全ログアウトしない） */
  const handlePinLogout = async () => {
    setActiveStaff(null);
    setCurrentUser(null);
  };

  // 【重要】タイムアウト監視フックを条件付きで呼び出す
  // ▼▼▼ 開発中の応急処置：タイムアウト監視を一時的に無効化 ▼▼▼
  /*
  const {
    isWarningShown,
    remainingWarningSeconds,
    resetTimer,
    continueSession,
  } = useIdleTimer({
    warningAfterMinutes: TIMEOUT_CONFIG.warningAfterMinutes,
    logoutAfterMinutes: TIMEOUT_CONFIG.logoutAfterMinutes,
    warningDurationSeconds: TIMEOUT_CONFIG.warningDurationSeconds,
    onWarning: () => {
      console.log('Timeout warning triggered');
    },
    onLogout: () => {
      console.log('Auto logout triggered');
      handleSignOut();
    },
    // 【追加】管理者の場合はタイマーを無効化
            disabled: currentUser?.role === 'admin' || currentUser?.role === 'developer',
  });
  */
  // ▲▲▲ ここまでコメントアウト ▲▲▲
  
  // 【開発用】タイムアウト関連の変数をダミーで定義
  const isWarningShown = false;
  const remainingWarningSeconds = 0;
  const resetTimer = () => {};
  const continueSession = () => {};

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      try {
        setAuthLoading(true);
        const { data, error } = await supabase.auth.getSession();
        if (error) throw error;
        const session = data.session;
        if (!session?.user?.id) {
          if (!cancelled) {
            setAuthUserId(null);
            setIsDeviceUser(false);
            setActiveStaff(null);
            setCurrentUser(null);
            setAuthUserIdInStore(null);
            setIsDeviceUserInStore(false);
            setAuthLoading(false);
            // Auth必須：未ログインなら/loginへ
            navigate('/login');
          }
          return;
        }
        if (requiresPasswordSetup(session.user)) {
          if (!cancelled) {
            setAuthLoading(false);
            navigate('/setup-password');
          }
          return;
        }
        if (!isPersonalLoginEnabled(session.user)) {
          await signOutAuth();
          if (!cancelled) {
            setAuthLoading(false);
            navigate('/login');
          }
          return;
        }
        const userId = session.user.id;
        if (cancelled) return;
        setAuthUserId(userId);
        setAuthUserIdInStore(userId);

        // Determine if this auth user is a device account for this clinic
        if (!clinicId) {
          setIsDeviceUser(false);
          setIsDeviceUserInStore(false);
          setAuthLoading(false);
          return;
        }

        const { data: cu, error: cuError } = await supabase
          .from('clinic_users')
          .select('user_kind, staff_id')
          .eq('auth_user_id', userId)
          .eq('clinic_id', clinicId)
          .eq('is_active', true)
          .maybeSingle();
        if (cuError) throw cuError;

        const kind = (cu as any)?.user_kind as string | undefined;
        const isDevice = kind === 'device';
        setIsDeviceUser(isDevice);
        setIsDeviceUserInStore(isDevice);

        if (isDevice) {
          // deviceアカウントは、PINで操作者を確定するまでスタッフ未確定
          setActiveStaff(null);
          setCurrentUser(null);
          setAuthLoading(false);
          return;
        }

        const clinicStaffId = (cu as any)?.staff_id as string | undefined;

        // personal staff/platform_admin access: map to a staff row for UI compatibility.
        // Prefer the staff_id from clinic_users so older data still resolves correctly.
        let staffRow: any = null;
        if (clinicStaffId) {
          const { data: staffByMembership, error: staffByMembershipError } = await supabase
            .from('staff')
            .select('*')
            .eq('id', clinicStaffId)
            .eq('clinic_id', clinicId)
            .eq('is_active', true)
            .maybeSingle();
          if (staffByMembershipError) throw staffByMembershipError;
          staffRow = staffByMembership;
        }

        if (!staffRow) {
          const { data: staffByAuthUser, error: staffByAuthUserError } = await supabase
            .from('staff')
            .select('*')
            .eq('auth_user_id', userId)
            .eq('clinic_id', clinicId)
            .eq('is_active', true)
            .maybeSingle();
          if (staffByAuthUserError) throw staffByAuthUserError;
          staffRow = staffByAuthUser;
        }

        if (!staffRow) {
          // No staff row yet: keep user signed-in but block app usage
          setActiveStaff(null);
          setCurrentUser(null);
          setStaffError('スタッフ情報が見つかりません。管理者に連絡してください。');
          setAuthLoading(false);
          return;
        }

        const normalizedRole = staffRow.role;
        const staff: Staff = {
          id: staffRow.id,
          createdAt: staffRow.created_at,
          updatedAt: staffRow.updated_at,
          name: staffRow.name,
          nameRomaji: staffRow.name_romaji,
          role: normalizedRole,
          pin: staffRow.pin,
          jobTitle: staffRow.job_title,
          phoneNumber: staffRow.phone_number || '',
          isActive: staffRow.is_active,
          createdBy: staffRow.created_by,
          updatedBy: staffRow.updated_by,
        };

        setActiveStaff(staff);
        setCurrentUser({
          id: staff.id,
          name: staff.name,
          role: staff.role as 'admin' | 'staff' | 'developer',
        });

        // まずパネル状態をリセットして「前ユーザーの残像」を出さない
        applyInitialPanelsForRole(staff);

        // Initialize clinic data
        await initialize();

        setAuthLoading(false);
      } catch (e) {
        console.error('Auth guard error:', e);
        setAuthLoading(false);
        navigate('/login');
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [
    clinicId,
    applyInitialPanelsForRole,
    initialize,
    isMobile,
    navigate,
    setCurrentUser,
    setAuthUserIdInStore,
    setIsDeviceUserInStore,
    setShowDatePanel,
    setShowDetailPanel,
    setShowWorkflowStaffListPanel,
    setScheduleSelectedStaffId,
    setSelectedDate,
  ]);

  const handleOperatorSelected = async (staff: Staff) => {
    setActiveStaff(staff);
    setCurrentUser({
      id: staff.id,
      name: staff.name,
      role: staff.role as 'admin' | 'staff' | 'developer',
    });
    resetTimer();
    // 先にリセットして残像を防止
    applyInitialPanelsForRole(staff);
    await initialize();
  };

  const handleTimeoutContinue = () => {
    continueSession();
  };

  const handleTimeoutLogout = () => {
    handleSignOut();
  };

  // 予約済みパスにアクセスした場合は何も表示しない（ルーティングで処理される）
  if (clinicId && RESERVED_PATHS.includes(clinicId)) {
    return null;
  }

  if (authLoading) {
    return (
      <div className="min-h-screen bg-blue-50 flex items-center justify-center">
        <div className="bg-white p-8 rounded-lg shadow-md">
          <div className="flex items-center space-x-3">
            <div className="w-6 h-6 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
            <span className="text-lg">読み込み中...</span>
          </div>
        </div>
      </div>
    );
  }

  // Auth session is mandatory. If user is logged in as a device account, require PIN for operator selection.
  if (authUserId && isDeviceUser && !activeStaff) {
    return (
      <PinLogin onSuccess={handleOperatorSelected} />
    );
  }

  if (!authUserId) {
    return null;
  }

  // スタッフ情報エラー（personalログインでstaffが見つからない場合）
  if (staffError && !activeStaff) {
    return (
      <div className="min-h-screen bg-blue-50 flex items-center justify-center p-4">
        <div className="bg-white p-8 rounded-lg shadow-md max-w-md w-full text-center space-y-4">
          <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md px-3 py-2">
            {staffError}
          </p>
          <button
            onClick={handleSignOut}
            className="px-4 py-2 text-sm text-teal-600 hover:underline"
          >
            ログアウト
          </button>
        </div>
      </div>
    );
  }

  if (!activeStaff) {
    return null;
  }

  return (
    <>
      <DashboardLayout 
        staff={activeStaff} 
        onLogout={isDeviceUser ? handlePinLogout : handleSignOut}
      />
      
      {/* Timeout Warning Dialog - 管理者でない場合のみ表示 */}
      {currentUser?.role !== 'admin' && isWarningShown && (
        <TimeoutWarning
          isOpen={isWarningShown}
          remainingSeconds={remainingWarningSeconds}
          onContinue={handleTimeoutContinue}
          onLogout={handleTimeoutLogout}
        />
      )}
    </>
  );
}

